'use client';

import { XMarkIcon } from '@heroicons/react/20/solid';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $patchStyleText } from '@lexical/selection';
import { $getRoot, $getSelection, $isRangeSelection } from 'lexical';

export function ClearFormattingButton({ onMarkdownSync, onRestoreSelection }) {
    const [editor] = useLexicalComposerContext();

    const expandSelectionToAllIfNone = () => {
        let sel = $getSelection();
        if (!$isRangeSelection(sel) || sel.isCollapsed()) {
            const root = $getRoot();
            root.select(0, root.getTextContentSize());
            sel = $getSelection();
        }
        return sel && $isRangeSelection(sel) ? sel : null;
    };

    const clearFormatting = () => {
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const sel = expandSelectionToAllIfNone();
            if (!sel) return;

            // 1) Remove inline color/gradient styles applied by TextColorButton
            $patchStyleText(sel, {
                '--editor-underline-color': null,
                '--editor-text-gradient-angle': null,
                '--editor-marker': null,
                '--editor-marker-color': null,
                '--editor-marker-gradient': null,
                '--editor-marker-angle': null,
                '--editor-marker-duration': null,
                '--editor-marker-delay': null,
                '--editor-marker-size': null,
                '--editor-marker-thickness': null,
                '--editor-marker-dot-size': null,
                '--editor-marker-radius': null,
                '--editor-marker-offset-y': null,
                '--editor-marker-replay': null,
                '--editor-marker-animate': null,
                '--editor-marker-svg': null,
                '--editor-marker-image': null,
                '--editor-marker-layer': null,
                '--editor-marker-direction': null,
                '--editor-line-height': null,
                'background-image': null,
                'background-size': null,
                'background-position': null,
                'background-repeat': null,
                '-webkit-background-clip': null,
                'background-clip': null,
                color: null,
                'text-decoration-color': null,
                'text-decoration-thickness': null,
                'border-bottom-style': null,
                'border-bottom-width': null,
                'border-image-source': null,
                'border-image-slice': null,
                'box-decoration-break': null,
                '-webkit-box-decoration-break': null,
                'font-family': null,
                'font-size': null,
                'letter-spacing': null,
            });

            // 2) Clear text format bits (bold/italic/underline/etc.) on text nodes
            const nodes = sel.getNodes();
            for (const n of nodes) {
                if (typeof n.setFormat === 'function') {
                    // 0 clears all format flags (bold, italic, underline, etc.)
                    n.setFormat(0);
                }
            }
            // Note: We intentionally DO NOT unwrap or modify LinkNodes,
            // so links remain intact.
        });
    };

    return (
        <button
            type="button"
            className="mdx-btn"
            title="Clear formatting (keep links)"
            aria-label="Clear formatting (keep links)"
            onMouseDown={(e) => {
                e.preventDefault(); // keep selection stable
                clearFormatting();
                onMarkdownSync?.();
            }}
        >
            <XMarkIcon className="h-[14px] w-[14px]" aria-hidden="true" />
        </button>
    );
}
